import type { AppTopic } from '@/backend/websocket'
import type { Channel, ChannelState } from 'phoenix'
import { onScopeDispose, ref, shallowRef, watch, type Ref } from 'vue'
import { useWebSocket } from './useWebSocket'

/**
 * Abstraction for subscribing to a websocket channel topic.
 *
 * As the topic changes, the channel will leave and rejoin.
 *
 * Exposes a shallow ref to the channel as well as a ref to the channel state.
 *
 * `channel.value.on` can be called to subscribe to messages on the channel.
 *
 * Channels are maintained in the shared `useWebSocket` composable using a unified
 * channel manager. This ensures that multiple subscriptions to the same topic
 * don't conflict with each other.
 *
 * @deprecated Consider using `useWebSocketTopic` for new code as it provides better
 * automatic cleanup and multi-subscriber support.
 */
export const useWebSocketChannel = (topic: Ref<AppTopic | null>) => {
  const channelState = ref<ChannelState>('closed')
  const ws = useWebSocket()
  const channel = shallowRef<Channel>()
  const isSubscribed = ref(false)

  // Update channel state based on WebSocket topic state
  const updateChannelState = () => {
    if (topic.value) {
      channelState.value = ws.getTopicState(topic.value)
    } else {
      channelState.value = 'closed'
    }
  }

  // Watch for topic changes and update the channel state
  watch(
    topic,
    async (newTopic, oldTopic) => {
      if (oldTopic === newTopic) {
        return
      }

      if (oldTopic && isSubscribed.value) {
        await ws.unsubscribeFromTopic(oldTopic)
        isSubscribed.value = false
      }

      if (!newTopic) {
        channel.value = undefined
        channelState.value = 'closed'
        return
      }

      try {
        const newChannel = await ws.getManagedChannel(newTopic)
        channel.value = newChannel
        isSubscribed.value = true
        updateChannelState()
      } catch {
        channel.value = undefined
        channelState.value = 'errored'
      }
    },
    { immediate: true },
  )

  // Sync channel state with the shared channel manager
  watch(
    () => (topic.value ? ws.getTopicState(topic.value) : 'closed'),
    (newState) => {
      channelState.value = newState
    },
  )

  const leaveChannel = async () => {
    if (!topic.value || !isSubscribed.value) {
      return
    }

    await ws.unsubscribeFromTopic(topic.value)
    isSubscribed.value = false
    channel.value = undefined
    channelState.value = 'closed'
  }

  onScopeDispose(async () => {
    await leaveChannel()
  }, true)

  return {
    channel,
    channelState,
    leaveChannel,
  }
}
